import * as Sentry from '@sentry/react';
import { Capacitor } from '@capacitor/core';

/**
 * Initialize Sentry error tracking and performance monitoring
 *
 * Supports both web and native platforms with appropriate configuration.
 * Native platforms (iOS/Android) get additional device context.
 */

const isNative = Capacitor.isNativePlatform();
const platform = Capacitor.getPlatform();

export function initTelemetry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    // Sentry DSN not configured, telemetry disabled
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE || 'development',
    release: `sunsquatch@${import.meta.env.VITE_APP_VERSION || '0.0.0'}`,

    // Performance monitoring - lower sampling on native to reduce overhead
    tracesSampleRate: import.meta.env.PROD
      ? isNative ? 0.05 : 0.1
      : 1.0,

    // Session replay (web only - not supported on native)
    replaysSessionSampleRate: isNative ? 0 : (import.meta.env.PROD ? 0.01 : 0.1),
    replaysOnErrorSampleRate: isNative ? 0 : 1.0,

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      // Only add replay on web
      ...(isNative ? [] : [
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ]),
    ],

    // Add platform and device info
    initialScope: {
      tags: {
        platform,
        isNative: String(isNative),
      },
    },

    // Filter out known non-critical errors
    beforeSend(event, hint) {
      const error = hint.originalException;

      // Ignore network errors (handled by offline mode)
      if (error instanceof TypeError && error.message?.includes('fetch')) {
        return null;
      }

      // Ignore ResizeObserver loop errors (browser quirk)
      if (error instanceof Error && error.message?.includes('ResizeObserver')) {
        return null;
      }

      // Ignore Capacitor plugin unavailable errors on web
      if (!isNative && error instanceof Error && error.message?.includes('not implemented')) {
        return null;
      }

      // Add native platform context
      if (isNative) {
        event.contexts = {
          ...event.contexts,
          device: {
            ...event.contexts?.device,
            platform,
          },
        };
      }

      return event;
    },

    // Scrub sensitive data
    beforeSendTransaction(event) {
      // Remove sensitive query parameters
      if (event.request?.url) {
        const url = new URL(event.request.url);
        url.searchParams.delete('token');
        url.searchParams.delete('code');
        event.request.url = url.toString();
      }
      return event;
    },
  });

  // Add platform-specific tags
  Sentry.setTag('platform', platform);
  Sentry.setTag('isNative', String(isNative));

  // Sentry initialized for ${platform} (native: ${isNative})
}

/**
 * Capture an exception with optional context
 */
export function captureException(
  error: Error,
  context?: Record<string, unknown>
): void {
  Sentry.captureException(error, {
    extra: {
      ...context,
      platform,
      isNative,
    },
  });
}

/**
 * Capture a message with optional severity
 */
export function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'info'
): void {
  Sentry.captureMessage(message, level);
}

/**
 * Set user context for error tracking
 */
export function setUser(user: { id: string; email?: string; username?: string } | null): void {
  Sentry.setUser(user);
}

/**
 * Add breadcrumb for error context
 */
export function addBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>
): void {
  Sentry.addBreadcrumb({
    category,
    message,
    data: {
      ...data,
      platform,
    },
    level: 'info',
  });
}

/**
 * Create a span for performance tracking
 */
export function startSpan<T>(
  name: string,
  operation: string,
  callback: () => T
): T {
  return Sentry.startSpan(
    {
      name,
      op: operation,
    },
    callback
  );
}

/**
 * Add native device context
 * Call this after Capacitor device info is available
 */
export async function addDeviceContext(): Promise<void> {
  if (!isNative) {
    return;
  }

  try {
    // Import Device plugin dynamically to avoid issues on web
    const { Device } = await import('@capacitor/device');
    const deviceInfo = await Device.getInfo();
    const deviceId = await Device.getId();

    Sentry.setContext('device_info', {
      model: deviceInfo.model,
      platform: deviceInfo.platform,
      operatingSystem: deviceInfo.operatingSystem,
      osVersion: deviceInfo.osVersion,
      manufacturer: deviceInfo.manufacturer,
      isVirtual: deviceInfo.isVirtual,
      memUsed: deviceInfo.memUsed,
      webViewVersion: deviceInfo.webViewVersion,
    });

    Sentry.setTag('device_id', deviceId.identifier);
    Sentry.setTag('device_model', deviceInfo.model);
    Sentry.setTag('os_version', deviceInfo.osVersion);

    // Added device context
  } catch (error) {
    // Failed to add device context: error
  }
}

/**
 * Track a native plugin error
 */
export function capturePluginError(
  pluginName: string,
  operation: string,
  error: Error
): void {
  Sentry.withScope((scope) => {
    scope.setTag('plugin', pluginName);
    scope.setTag('operation', operation);
    scope.setLevel('error');
    Sentry.captureException(error);
  });
}

/**
 * Track app lifecycle events
 */
export function trackAppStateChange(isActive: boolean): void {
  addBreadcrumb(
    'app.lifecycle',
    isActive ? 'App became active' : 'App went to background',
    { isActive }
  );
}

// Export Sentry React components for error boundaries
export { ErrorBoundary } from '@sentry/react';
